import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  useTheme,
  TextField,
  Chip,
  Switch,
  Avatar,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { Search, Clear, Visibility } from "@mui/icons-material";
import Header from "components/Header";
import { DataGrid } from "@mui/x-data-grid";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import { getAthletes, toggleFacilityOwner } from "service/admin/userService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AthleteUsers = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [facilityOwnersOnly, setFacilityOwnersOnly] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    athleteId: null,
    newValue: false,
    name: "",
  });

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        page: page + 1,
        limit: pageSize,
        search: search || undefined,
      };
      if (facilityOwnersOnly) params.is_facility_owner = true;
      const response = await getAthletes(params);
      const data = response.data.data;
      setRows(Array.isArray(data) ? data : data.data || []);
      if (data.pagination?.total !== undefined) setRowCount(data.pagination.total);
    } catch (error) {
      toast.error("Failed to load athletes.");
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, search, facilityOwnersOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(0);
  };

  const handleFacilityOwnerToggleClick = (id, currentValue, name) => {
    setConfirmDialog({ open: true, athleteId: id, newValue: !currentValue, name });
  };

  const handleConfirmToggle = async () => {
    const { athleteId, newValue } = confirmDialog;
    setConfirmDialog({ open: false, athleteId: null, newValue: false, name: "" });
    try {
      await toggleFacilityOwner(athleteId, newValue);
      setRows((prev) =>
        prev.map((r) =>
          r.id === athleteId ? { ...r, is_facility_owner: newValue } : r
        )
      );
      toast.success(`Facility owner status updated.`);
    } catch (error) {
      toast.error("Failed to update facility owner status.");
    }
  };

  const statusChip = (status) => {
    const map = {
      active: { label: "Active", color: "success" },
      inactive: { label: "Inactive", color: "default" },
      suspended: { label: "Suspended", color: "error" },
    };
    const cfg = map[status] || { label: status || "Unknown", color: "default" };
    return <Chip label={cfg.label} size="small" color={cfg.color} />;
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.NO",
      flex: 0.4,
      sortable: false,
      renderCell: (params) =>
        page * pageSize + params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "profile_dp",
      headerName: "PIC",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <Avatar
          src={params.value}
          alt={params.row.full_name}
          sx={{ width: 32, height: 32 }}
        />
      ),
    },
    {
      field: "full_name",
      headerName: "NAME",
      flex: 1,
    },
    {
      field: "username",
      headerName: "USERNAME",
      flex: 1,
    },
    {
      field: "phone_no",
      headerName: "PHONE",
      flex: 1,
    },
    {
      field: "email_id",
      headerName: "EMAIL",
      flex: 1.2,
    },
    {
      field: "account_status",
      headerName: "STATUS",
      flex: 0.8,
      renderCell: (params) => statusChip(params.value),
    },
    {
      field: "is_facility_owner",
      headerName: "FACILITY OWNER",
      flex: 1,
      renderCell: (params) => (
        <Switch
          color="secondary"
          size="small"
          checked={!!params.value}
          onChange={() =>
            handleFacilityOwnerToggleClick(
              params.row.id,
              !!params.value,
              params.row.full_name
            )
          }
        />
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 0.6,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="secondary"
          onClick={() => {}}
          title="View"
        >
          <Visibility fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <ToastContainer />
      <Box m="1.5rem 2.5rem">
        <Header title="ATHLETE USERS" subTitle="List of Athlete Users" />

        <Box mt="1rem" mb="0.5rem" display="flex" alignItems="center" gap={2}>
          <TextField
            size="small"
            placeholder="Search by name, phone, email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            color="secondary"
            sx={{ width: 300 }}
            InputProps={{
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton onClick={handleSearch} color="secondary">
            <Search />
          </IconButton>
          <FormControlLabel
            control={
              <Switch
                color="secondary"
                checked={facilityOwnersOnly}
                onChange={(e) => {
                  setFacilityOwnersOnly(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Facility Owners Only"
            sx={{ color: theme.palette.secondary[200] }}
          />
        </Box>

        <Box
          mt="10px"
          height="70vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          <DataGrid
            loading={isLoading}
            getRowId={(row) => row.id}
            rows={rows}
            columns={columns}
            rowCount={rowCount || rows.length}
            rowsPerPageOptions={[10, 25, 50]}
            pagination
            page={page}
            pageSize={pageSize}
            paginationMode={rowCount > 0 ? "server" : "client"}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(0);
            }}
            components={{ Toolbar: DataGridCustomToolbar }}
            sx={{
              "& .MuiCircularProgress-circle": {
                color: theme.palette.secondary[300],
              },
            }}
          />
        </Box>
      </Box>

      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({ open: false, athleteId: null, newValue: false, name: "" })
        }
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.newValue
              ? `Set ${confirmDialog.name} as a facility owner?`
              : `Remove facility owner status from ${confirmDialog.name}?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({
                open: false,
                athleteId: null,
                newValue: false,
                name: "",
              })
            }
          >
            Cancel
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleConfirmToggle}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AthleteUsers;
